import React, { useMemo, useState } from "react";
import { Meta } from "../layout/Meta";
import Link from "../components/Link";
import Container from "../components/ui/Container";
import Eyebrow from "../components/ui/Eyebrow";
import { BROKERS, INTEGRATIONS_PATH } from "../content/brokers";
import BrokerLogo from "../components/home/BrokerLogo";

const PATH = INTEGRATIONS_PATH;
const TITLE = "Brokerage integrations";
const DESCRIPTION =
  "See the brokerage connections Stratify can attach through SnapTrade. Availability and permissions vary by broker.";

export default function IntegrationsPage() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BROKERS;
    return BROKERS.filter(
      (broker) =>
        broker.name.toLowerCase().includes(q) ||
        broker.description.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="w-full bg-bg0">
      <Meta
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        keywords={[
          "brokerage integrations",
          "SnapTrade",
          "connect brokerage",
          "Stratify Backtesting",
        ]}
      />

      <section className="border-b border-ink/8 bg-bg1 py-16 md:py-20">
        <Container>
          <p className="text-[13px] font-medium text-muted">
            Product
            <span className="mx-2 text-ink/25" aria-hidden="true">
              /
            </span>
            <span className="text-hint-deep">Integrations</span>
          </p>
          <Eyebrow className="mt-8">SnapTrade connections</Eyebrow>
          <h1 className="mt-4 max-w-3xl text-4xl font-semibold tracking-display text-ink md:text-6xl">
            Brokerage connections you can attach.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted md:text-lg">
            Supported accounts can bring holdings into the same view as your saved
            strategies. Connections are provided through SnapTrade. Availability and
            permissions vary by broker.
          </p>
          <p className="mt-3 max-w-2xl text-sm text-muted">
            Connecting an account does not mean Stratify places live orders.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/connect-brokerage"
              className="inline-flex text-sm font-medium text-hint-deep hover:underline"
            >
              How to connect your brokerage account ↗
            </Link>
            <span className="text-ink/20">·</span>
            <span className="text-sm text-muted">{BROKERS.length} integrations</span>
          </div>
        </Container>
      </section>

      <section className="py-12 md:py-16">
        <Container>
          <label className="block max-w-md">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
              Search integrations
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search brokers"
              className="mt-3 block w-full rounded-2xl border border-ink/8 bg-white px-4 py-3 text-sm text-ink outline-none ring-0 placeholder:text-muted/70 focus:border-hint-deep/40 focus:ring-2 focus:ring-hint/30"
            />
          </label>

          {filtered.length === 0 ? (
            <p className="mt-10 text-sm text-muted">
              No integrations match “{query.trim()}”.
            </p>
          ) : (
            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((broker) => (
                <li key={broker.id}>
                  <article className="flex h-full gap-4 rounded-[24px] border border-ink/8 bg-white p-5 shadow-card">
                    <BrokerLogo broker={broker} size="lg" />
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold tracking-tight text-ink">
                        {broker.name}
                      </h2>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted">
                        {broker.description}
                      </p>
                    </div>
                  </article>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-10 text-[11px] text-muted">
            Broker names and logos are trademarks of their respective owners. Support
            can change as SnapTrade coverage changes.
          </p>
        </Container>
      </section>
    </div>
  );
}
